import cloneDeep from 'clone-deep'
import { sumBy, TotalsRow } from 'components/TableTotals'
import type { InputType } from 'config'
import { useEffect, useState } from 'react'
import { Modal } from 'stories/components'
import { formatDate, InputConvert, InputValidate, thousandSeperator } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { AccountTransactionStatus, IAccountTransaction } from './types'

interface ReconcileDialogProps {
  isOpen: boolean
  onClose: (reconciliationData?: {
    transactionIds: number[]
    statementDate: string
    endingBalance: number
    serviceCharge: number
    interest: number
  }) => void
  transactions: IAccountTransaction[]
  accountName: string
  beginningBalance: number
}

export const ReconcileDialog = ({
  isOpen,
  onClose,
  transactions,
  accountName,
  beginningBalance,
}: ReconcileDialogProps) => {
  const [isLoading, setLoading] = useState(false)
  const [checkedTransactions, setCheckedTransactions] = useState<Set<number>>(new Set())
  const [inputs, setInputs] = useState<Record<string, InputType>>({
    endingDate: {
      inputType: 'text',
      type: 'date',
      title: 'Statement Ending Date',
      required: true,
      value: '',
    },
    endingBalance: {
      inputType: 'text',
      type: 'number',
      title: 'Ending Balance',
      required: true,
      value: '',
    },
    // serviceCharge: {
    //   inputType: 'text',
    //   type: 'number',
    //   title: 'Service Charge',
    //   required: false,
    //   value: '0',
    // },
    // interest: {
    //   inputType: 'text',
    //   type: 'number',
    //   title: 'Interest Earned',
    //   required: false,
    //   value: '0',
    // },
  })

  // Initialize checked transactions with already checked/reconciled items
  useEffect(() => {
    const initialChecked = new Set<number>()
    transactions.forEach((txn) => {
      if (txn.status === AccountTransactionStatus.CHECKED || txn.status === AccountTransactionStatus.RECONCILED) {
        initialChecked.add(txn.id)
      }
    })
    setCheckedTransactions(initialChecked)
  }, [transactions])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const toggleTransaction = (id: number) => {
    const newChecked = new Set(checkedTransactions)
    if (newChecked.has(id)) {
      newChecked.delete(id)
    } else {
      newChecked.add(id)
    }
    setCheckedTransactions(newChecked)
  }

  // Calculate totals
  const unreconciledTransactions = transactions.filter((t) => t.status !== AccountTransactionStatus.RECONCILED)

  const paymentsCleared = unreconciledTransactions
    .filter((t) => checkedTransactions.has(t.id))
    .reduce((sum, t) => sum + Number(t.payment || 0), 0)

  const depositsCleared = unreconciledTransactions
    .filter((t) => checkedTransactions.has(t.id))
    .reduce((sum, t) => sum + Number(t.deposit || 0), 0)

  const clearedBalance = beginningBalance + depositsCleared - paymentsCleared
  const serviceCharge = Number(inputs.serviceCharge?.value || 0)
  const interest = Number(inputs.interest?.value || 0)
  const adjustedBalance = clearedBalance - serviceCharge + interest
  const endingBalance = Number(inputs.endingBalance.value || 0)
  const difference = adjustedBalance - endingBalance

  const onSubmit = async () => {
    let hasError = false
    let newInputs = cloneDeep(inputs)

    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      if (newInputs[key].error) hasError = true
    }

    if (hasError) {
      setInputs(newInputs)
      return
    }

    if (Math.abs(difference) > 0.01) {
      alert(
        `Cannot reconcile: Difference is $${difference.toFixed(
          2,
        )}. The difference must be $0.00 to complete reconciliation.`,
      )
      return
    }

    setLoading(true)
    const alreadyReconciledIds = new Set(
      transactions.filter((t) => t.status === AccountTransactionStatus.RECONCILED).map((t) => t.id),
    )
    const newlyReconciledIds = Array.from(checkedTransactions).filter((id) => !alreadyReconciledIds.has(id))
    await onClose({
      transactionIds: newlyReconciledIds,
      statementDate: inputs.endingDate.value,
      endingBalance: Number(inputs.endingBalance.value),
      serviceCharge,
      interest,
    })
    setLoading(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      title={`Reconcile ${accountName}`}
      loading={isLoading}
      onOk={onSubmit}
      onClose={() => onClose()}
      titleOkay="Finish Now"
      titleCancel="Cancel"
    >
      <div className="w-[1200px]">
        {/* Statement Information */}
        <div className="bg-gray-50 p-4 rounded mb-4">
          <h3 className="font-semibold mb-3">Statement Information</h3>
          <div className="grid grid-cols-2 gap-4">
            {Object.keys(inputs).map((key) => (
              <div key={key}>
                <RenderInput input={inputs[key]} Key={key} onChange={onChange} />
              </div>
            ))}
          </div>
        </div>

        {/* Balance Summary */}
        <div className="bg-blue-50 p-4 rounded mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-2">Statement</h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Beginning Balance:</span>
                  <span>${thousandSeperator(beginningBalance)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Ending Balance:</span>
                  <span className="font-semibold">${thousandSeperator(endingBalance)}</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Cleared</h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span>
                    Payments (
                    {unreconciledTransactions.filter((t) => checkedTransactions.has(t.id) && t.payment).length}):
                  </span>
                  <span>${thousandSeperator(paymentsCleared)}</span>
                </div>
                <div className="flex justify-between">
                  <span>
                    Deposits (
                    {unreconciledTransactions.filter((t) => checkedTransactions.has(t.id) && t.deposit).length}):
                  </span>
                  <span>${thousandSeperator(depositsCleared)}</span>
                </div>
                {serviceCharge > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Service Charge:</span>
                    <span>-${thousandSeperator(serviceCharge)}</span>
                  </div>
                )}
                {interest > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Interest:</span>
                    <span>+${thousandSeperator(interest)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold border-t pt-1">
                  <span>Cleared Balance:</span>
                  <span>${thousandSeperator(adjustedBalance)}</span>
                </div>
                <div
                  className={`flex justify-between font-bold text-lg ${
                    Math.abs(difference) < 0.01 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  <span>Difference:</span>
                  <span>${thousandSeperator(difference)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div className="max-h-96 overflow-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left w-10">✓</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Ref No</th>
                <th className="px-3 py-2 text-left">Payee</th>
                <th className="px-3 py-2 text-right">Payment</th>
                <th className="px-3 py-2 text-right">Deposit</th>
                <th className="px-3 py-2 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {unreconciledTransactions.map((txn) => {
                const isChecked = checkedTransactions.has(txn.id)
                const isReconciled = txn.status === AccountTransactionStatus.RECONCILED
                return (
                  <tr key={txn.id} className={`border-b hover:bg-gray-50 ${isReconciled ? 'opacity-50' : ''}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => !isReconciled && toggleTransaction(txn.id)}
                        disabled={isReconciled}
                        className="w-4 h-4 cursor-pointer"
                      />
                    </td>
                    <td className="px-3 py-2">{formatDate(txn.date)}</td>
                    <td className="px-3 py-2">{txn.refNo}</td>
                    <td className="px-3 py-2">{txn.payee}</td>
                    <td className="px-3 py-2 text-right">
                      {Number(txn.payment) ? `$${thousandSeperator(txn.payment)}` : ''}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {Number(txn.deposit) ? `$${thousandSeperator(txn.deposit)}` : ''}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs ${isReconciled ? 'text-green-600 font-bold' : ''}`}>
                        {txn.status === AccountTransactionStatus.RECONCILED ? 'R' : isChecked ? 'C' : ''}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {unreconciledTransactions.length > 0 && (
              <TotalsRow
                tdClass="px-3 py-2"
                cells={[
                  '',
                  'Totals',
                  '',
                  '',
                  sumBy(unreconciledTransactions, 'payment'),
                  sumBy(unreconciledTransactions, 'deposit'),
                  '',
                ]}
              />
            )}
          </table>
        </div>

        {Math.abs(difference) < 0.01 && endingBalance > 0 && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-green-700">
            ✓ Ready to reconcile! The difference is $0.00
          </div>
        )}
        {Math.abs(difference) >= 0.01 && endingBalance > 0 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-700">
            ⚠ Difference must be $0.00 to complete reconciliation
          </div>
        )}
      </div>
    </Modal>
  )
}
